<template>
  <div id="listWidget" class="mb-4">
    <div class="instruction-card card-item mb-3">
      <div class="instruction-header">
        <i class="fa-solid fa-map-location-dot me-2 text-primary"></i>
        <span class="instruction-title">{{ props.step }}. 영역 선택</span>
      </div>
      <div class="instruction-body">
        <div class="instruction-step">
          <div class="instruction-step-number">1</div>
          <div class="instruction-step-content">
            <span>목록에서 컨트롤러를 등록할 <strong class="text-primary">영역을 선택</strong>해주세요. 영역이 없으면
              아래의 <strong class="text-primary">영역 버튼</strong>을 눌러
              <strong class="text-primary">영역을 추가</strong>해주세요.</span>
          </div>
        </div>
        <div class="instruction-step">
          <div class="instruction-step-number">2</div>
          <div class="instruction-step-content">
            <span>아래의 <strong class="text-primary">다음 버튼</strong>을 눌러서 컨트롤러 등록
              단계에 진입해주세요.</span>
          </div>
        </div>
      </div>
    </div>

    <!-- 영역 추가 버튼 -->
    <div class="instruction-card card-item mb-3">
      <div class="instruction-header">
        <i class="fa-solid fa-plus me-2 text-primary"></i>
        <span class="instruction-title">영역 추가</span>
      </div>
      <div class="instruction-body">
        <button
          type="button"
          class="btn btn-outline-primary mb-1 me-1"
          style="min-width: 50px"
          v-for="(item, index) in templateZoneNames"
          :key="index"
          @click="onAddZoneClick(item)"
        >
          {{ item }}
        </button>
      </div>
    </div>

    <!-- 영역 선택 리스트 -->
    <div class="instruction-card card-item mb-3" v-if="props.zones?.length > 0">
      <div class="instruction-header">
        <i class="fa-solid fa-list me-2 text-primary"></i>
        <span class="instruction-title">영역 선택</span>
      </div>
      <div class="instruction-body">
        <div class="zone-list">
          <div
            class="zone-item"
            v-for="(zone, index) in props.zones"
            :key="zone._id"
          >
            <div class="form-check flex-1 text-break">
              <input
                class="form-check-input"
                type="checkbox"
                :id="`gridCheck${index}`"
                :value="zone._id"
                @change="props.handleChange"
                :checked="zone._id == props.selectedZone?._id"
              />
              <label class="form-check-label" :for="`gridCheck${index}`">
                {{ zone.name }}
              </label>
            </div>
            <i
              class="fa-regular fa-trash-can zone-delete-icon"
              v-if="zone.controllers.length === 0"
              @click="() => onDeleteZoneClick(zone._id)"
            ></i>
          </div>
        </div>
      </div>
    </div>

    <PopDialog
      title="영역추가"
      :visible="addZoneModal.visible"
      :okCallback="onAddZoneConfirmClick"
      :cancelCallback="addZoneModal.hide"
    >
      <input
        type="text"
        class="form-control form-control-lg fs-15px"
        placeholder="영역 이름을 입력해주세요."
        v-model="addZoneName"
      />
    </PopDialog>
  </div>
</template>

<style scoped>
.instruction-card {
  background-color: #ffffff;
  border: 1px solid #e9ecef;
  border-radius: 1.2rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  overflow: hidden;
}

.instruction-header {
  display: flex;
  align-items: center;
  padding: 0.75rem 1rem;
  background-color: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
}

.instruction-title {
  font-size: 0.9rem;
  font-weight: 600;
  color: #212529;
}

.instruction-body {
  padding: 0.75rem 1rem;
}

.instruction-step {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
}

.instruction-step:last-child {
  margin-bottom: 0;
}

.instruction-step-number {
  width: 1.5rem;
  height: 1.5rem;
  min-width: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #0d6efd;
  color: white;
  border-radius: 50%;
  font-weight: 600;
  font-size: 0.75rem;
}

.instruction-step-content {
  flex: 1;
  padding-top: 0.1rem;
  font-size: 0.85rem;
  line-height: 1.5;
  color: #495057;
}

.instruction-step-content .text-primary {
  color: #0d6efd;
  font-weight: 600;
}

.zone-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.zone-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem;
  background-color: #f8f9fa;
  border-radius: 0.5rem;
  border: 1px solid #e9ecef;
  transition: all 0.2s ease;
}

.zone-item:hover {
  background-color: #e9ecef;
  border-color: #dee2e6;
}

.zone-delete-icon {
  color: #dc3545;
  cursor: pointer;
  padding: 0.25rem;
  transition: all 0.2s ease;
}

.zone-delete-icon:hover {
  color: #bb2d3b;
  transform: scale(1.1);
}
</style>

<script setup>
import { ref, defineProps, computed, getCurrentInstance, reactive } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { Template } from '@/common/constants';
import Axios, { ZoneApi } from '@/api';
import PopDialog from '@/common/components/popup/PopDialog.vue';
import useModal from '@/common/hooks/useModal';
import CardBody from '@/common/template/components/bootstrap/CardBody.vue';
import ListGroupItem from '@/common/components/widgets/ListGroupItem.vue';

const { emitter, mobile, logger } =
  getCurrentInstance().appContext.config.globalProperties;
const props = defineProps(['step', 'handleChange', 'zones', 'selectedZone']);
const store = useStore();
const router = useRouter();
const addZoneModal = reactive(useModal());
const templateZoneNames = computed(() => Template.ZoneNames);
let addZoneName = ref('');

const onAddZoneClick = (name) => {
  if (name === Template.ZoneNames[Template.ZoneNames.length - 1]) {
    addZoneModal.show();
    return;
  }

  requestAddZone(name);
};

const onDeleteZoneClick = (zoneId) => {
  requestDeleteZone(zoneId);
};

const onAddZoneConfirmClick = async () => {
  if (addZoneName.value.length === 0) {
    emitter.emit('showPopAlert', {
      description: "영역 이름을 입력해주세요.",
    });
    return;
  }

  addZoneModal.hide();
  requestAddZone(addZoneName.value);
};

const requestAddZone = async (name) => {
  try {
    const result = await Axios.post(ZoneApi.REGIST_ZONE, { name: name });
    if (result.resultCode === '0000') {
      store.commit('home/addZone', result.resultData);
      return { zone: result.resultData, result: true };
    }

    emitter.emit('showPopAlert', {
      description: result.resultMessage,
    });
    return { result: false, zone: undefined };
  } catch (e) {
    console.log(e);
    emitter.emit('showPopAlert', {
      description: '영역 추가 중 오류가 발생하였습니다.',
    });
  }
};

const requestDeleteZone = async (zoneId) => {
  try {
    const result = await Axios.delete(
      ZoneApi.DELETE_ZONE.replace(':id', zoneId),
    );
    if (result.resultCode === '0000') {
      store.commit('home/deleteZone', zoneId);
      return;
    }

    emitter.emit('showPopAlert', {
      description: result.resultMessage,
    });
  } catch (error) {
    emitter.emit('showPopAlert', {
      description: '영역 삭제 중 오류가 발생하였습니다.',
    });
  }
};
</script>
